import React, { useEffect, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

const SHORT_SIDE = 768;

const PALETTES = {
  home: {
    dark: ['#111A33', '#080E1D', '#050913'],
    light: ['#62B8E8', '#A8DCF4', '#E4F6FD'],
  },
  courses: {
    dark: ['#15213B', '#0D1426', '#070B16'],
    light: ['#56AFE0', '#9BD4EF', '#DDF3FC'],
  },
};

/** Draw one soft cloud without loading another image or running a cloud shader
 * over every screen pixel. The radial puffs keep the texture inexpensive and
 * also avoid the hard rectangle edges that alpha sprites can show on mobile. */
const drawCloud = (ctx, x, y, width, opacity) => {
  const puff = (px, py, rx, ry, alpha) => {
    ctx.save();
    ctx.translate(px, py);
    ctx.scale(rx, ry);
    const gradient = ctx.createRadialGradient(0, 0, 0.08, 0, 0, 1);
    gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
    gradient.addColorStop(0.58, `rgba(250,254,255,${alpha * 0.9})`);
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, 1, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  };

  puff(x - width * 0.24, y + width * 0.02, width * 0.34, width * 0.12, opacity * 0.72);
  puff(x, y, width * 0.42, width * 0.15, opacity * 0.82);
  puff(x - width * 0.1, y - width * 0.08, width * 0.2, width * 0.16, opacity);
  puff(x + width * 0.16, y - width * 0.06, width * 0.24, width * 0.18, opacity * 0.94);
  puff(x + width * 0.3, y + width * 0.01, width * 0.28, width * 0.11, opacity * 0.68);
};

const makeBackdrop = (theme, variant, aspect) => {
  const canvas = document.createElement('canvas');
  // three.js stretches a 2D scene background across the viewport. Match that
  // viewport here so the cloud itself keeps the same proportions everywhere.
  canvas.width = Math.round(aspect >= 1 ? SHORT_SIDE * aspect : SHORT_SIDE);
  canvas.height = Math.round(aspect >= 1 ? SHORT_SIDE : SHORT_SIDE / aspect);
  const ctx = canvas.getContext('2d');
  const colors = PALETTES[variant]?.[theme] || PALETTES.home.dark;
  const { width, height } = canvas;
  const cloudSize = Math.min(width, height);

  const sky = ctx.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, colors[0]);
  sky.addColorStop(0.55, colors[1]);
  sky.addColorStop(1, colors[2]);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(width * 0.72, height * 0.12, 0, width * 0.72, height * 0.12, cloudSize * 0.5);
  glow.addColorStop(0, theme === 'light' ? 'rgba(255,250,218,0.48)' : 'rgba(83,111,190,0.16)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  if (theme === 'light') {
    // Kept around the edges so the models remain the focal point. The texture
    // is a scene background, so these stay infinitely far away as the camera
    // flies between course islands.
    drawCloud(ctx, width * 0.12, height * 0.2, cloudSize * 0.27, 0.64);
    drawCloud(ctx, width * 0.86, height * 0.31, cloudSize * 0.25, 0.58);
    drawCloud(ctx, width * 0.2, height * 0.66, cloudSize * 0.32, 0.32);
    drawCloud(ctx, width * 0.78, height * 0.76, cloudSize * 0.36, 0.28);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
};

export const useSceneBackdrop = (theme, variant = 'home') => {
  const { width, height } = useThree((state) => state.size);
  // Bucket tiny layout changes so a sub-pixel resize does not rebuild a large
  // texture, while staying close enough that any remaining stretch is unseen.
  const aspect = Math.round(
    THREE.MathUtils.clamp(width / Math.max(height, 1), 0.5, 2.5) * 50
  ) / 50;
  const texture = useMemo(
    () => makeBackdrop(theme, variant, aspect),
    [theme, variant, aspect]
  );
  useEffect(() => () => texture.dispose(), [texture]);
  return texture;
};

const SceneBackdrop = ({ theme, variant = 'home' }) => {
  const texture = useSceneBackdrop(theme, variant);
  return <primitive attach="background" object={texture} />;
};

export default SceneBackdrop;
